import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, Secondary } from '../../../../core-components/theme/color-palette/colors';
import { SurfaceCard } from '../../../../core-components/theme/components/SurfaceCard';
import { alwaysReachesLabel } from '../../constants/labels';
import { proseSx } from '../../constants/layout';
import type { AlwaysReachesKey } from '../../types/domain.types';

export type AlwaysReachesVariant = 'card' | 'list';

export interface AlwaysReachesLineProps {
  items: readonly AlwaysReachesKey[];
  variant?: AlwaysReachesVariant;
}

const cardSx = { backgroundColor: Secondary[50], borderColor: Secondary[100], display: 'flex', flexDirection: 'column', gap: 1 } as const;
const cardTitleSx = { color: Neutrals[900] } as const;
const cardListSx = [proseSx, { color: Neutrals[700] }] as const;

const listSx = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 } as const;
const listItemSx = { display: 'flex', alignItems: 'flex-start', gap: 2 } as const;
const listIconSx = { flexShrink: 0, color: Secondary[600] } as const;
const listTextSx = { color: Neutrals[900] } as const;
const emptySx = [proseSx, { color: Neutrals[700] }] as const;

const labelsOf = (t: TFunction, items: readonly AlwaysReachesKey[]): string[] => items.map((item) => alwaysReachesLabel(t, item));

const AlwaysReachesCard: React.FC<{ items: readonly AlwaysReachesKey[] }> = ({ items }) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <SurfaceCard sx={cardSx}>
      <Typography variant="highlight" component="p" sx={cardTitleSx}>
        {t('closeTheLoop.settings.alwaysReachesYou', 'Always reaches you')}
      </Typography>
      <Typography variant="caption2" component="p" sx={cardListSx}>
        {labelsOf(t, items).join(' · ')}
      </Typography>
    </SurfaceCard>
  );
};

const AlwaysReachesList: React.FC<{ items: readonly AlwaysReachesKey[] }> = ({ items }) => {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <Typography variant="body" component="p" sx={emptySx}>
        {t('closeTheLoop.alwaysReaches.none', 'Nothing is forced on you — you choose everything next.')}
      </Typography>
    );
  }
  return (
    <Box component="ul" sx={listSx}>
      {items.map((item) => (
        <Box component="li" key={item} sx={listItemSx}>
          <CheckCircleOutlineIcon fontSize="small" aria-hidden sx={listIconSx} />
          <Typography variant="body" component="span" sx={listTextSx}>
            {alwaysReachesLabel(t, item)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const AlwaysReachesLine: React.FC<AlwaysReachesLineProps> = ({ items, variant = 'card' }) => {
  switch (variant) {
    case 'list':
      return <AlwaysReachesList items={items} />;
    case 'card':
      return <AlwaysReachesCard items={items} />;
  }
};
